import React from 'react';
import { RotateCw, Search } from 'lucide-react';
import { Spinner } from '../ui';
import { RowTone, rowStateOf } from './researchProgress';
import { ComponentListItem } from './discoveryModel';
import { useReadOnly } from '../../contexts/WizardAccessContext';

/**
 * Discovery master list (left pane of the mock's master–detail): filter box on
 * top, one row per component — name, mono part id, and an honest state chip
 * (researching / waiting / trying again / N suppliers / none found / failed · retry).
 */
interface ComponentListPaneProps {
  items: ComponentListItem[];
  totalCount: number;
  selectedKey: string | null;
  filter: string;
  onFilterChange: (value: string) => void;
  onSelect: (key: string) => void;
  onRetry: (key: string) => void;
  retrying: Record<string, boolean>;
}

const TONE: Record<RowTone, string> = {
  running: 'bg-robbie-bg text-robbie-ink',
  retrying: 'bg-warn-bg text-warn-ink',
  waiting: 'bg-neutral-bg text-neutral-ink',
  ready: 'bg-success-bg text-success-ink',
  picked: 'bg-success-bg text-success-ink',
  empty: 'bg-neutral-bg text-neutral-ink',
  failed: 'bg-danger-bg text-danger-ink',
  review: 'bg-warn-bg text-warn-ink',
};

const StateChip: React.FC<{ item: ComponentListItem }> = ({ item }) => {
  const row = rowStateOf(item);
  return (
    <span className={`inline-flex items-center gap-s1 rounded-[4px] px-s2 py-[1px] text-meta font-semibold ${TONE[row.tone]}`}>
      {row.busy && <Spinner tone="robbie" size="sm" />}
      {row.label}
    </span>
  );
};

export const ComponentListPane: React.FC<ComponentListPaneProps> = ({
  items,
  totalCount,
  selectedKey,
  filter,
  onFilterChange,
  onSelect,
  onRetry,
  retrying,
}) => {
  const readOnly = useReadOnly();

  return (
  <div className="flex flex-col overflow-hidden border-r border-line max-md:border-r-0">
    <div className="shrink-0 border-b border-line-divider-soft px-[12px] py-[10px]">
      <div className="flex items-center gap-s3 rounded-control border border-line-strong px-s4 py-[7px]">
        <Search className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
        <input
          type="text"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Filter components…"
          className="w-full border-none bg-transparent font-sans text-body-sm text-ink outline-none placeholder:text-ink-muted"
        />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto">
      {items.map((item) => {
        const active = item.key === selectedKey;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            className={`block w-full border-b border-line-divider px-[12px] py-[10px] text-left ${
              active ? 'border-l-[2.5px] border-l-action bg-action-bg' : 'hover:bg-surface-hover'
            }`}
          >
            <div className={`text-[12.5px] font-semibold leading-[1.35] ${active ? 'text-action-deep' : 'text-ink'}`}>
              {item.name}
            </div>
            <div className="mt-[3px] flex flex-wrap items-center gap-x-s2 gap-y-[2px]">
              {item.partNumber && <span className="font-mono text-[10.5px] text-ink-muted">{item.partNumber}</span>}
              {item.mpn && <span className="font-mono text-[10.5px] text-ink-muted">MPN {item.mpn}</span>}
              <StateChip item={item} />
              {item.phase === 'failed' && !readOnly && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Retry research for ${item.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!retrying[item.key]) onRetry(item.key);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      if (!retrying[item.key]) onRetry(item.key);
                    }
                  }}
                  className="ml-auto inline-flex rounded-[4px] p-[2px] text-danger-ink hover:bg-danger-bg"
                >
                  {retrying[item.key] ? <Spinner tone="action" size="sm" /> : <RotateCw className="h-3 w-3" />}
                </span>
              )}
            </div>
          </button>
        );
      })}
      <div className="px-[12px] py-[10px] font-mono text-meta text-ink-muted">
        {items.length === totalCount ? `${totalCount} components` : `${items.length} of ${totalCount} components`}
      </div>
    </div>
  </div>
  );
};

export default ComponentListPane;
